import { useReducer, useMemo, type ReactNode } from 'react';
import {
  initialCatalog,
  CatalogStateContext,
  CatalogDispatchContext,
  ProductsContext,
  ViewContext,
  UxContext,
  CartContext,
} from './CatalogContext';
import type { CatalogAction, CatalogState } from '../types/store';

const catalogReducer = (state: CatalogState, action: CatalogAction): CatalogState => {
  // console.log('catalogContext', state, action);

  switch (action.type) {
    case 'UPDATE': {
      return {
        ...state,
        ...action.payload,
        isError: false,
      };
    }
    case 'UPDATE_PRODUCTS': {
      return {
        ...state,
        isLoading: false,
        products: {
          ...state.products,
          ...action.payload,
        },
      };
    }
    case 'UPDATE_CART': {
      return {
        ...state,
        cart: {
          ...state.cart,
          ...action.payload,
        },
      };
    }
    case 'UX': {
      return {
        ...state,
        ux: {
          ...state.ux,
          ...action.payload,
        },
      };
    }
    case 'VIEW': {
      return {
        ...state,
        view: {
          ...state.view,
          ...action.payload,
        },
      };
    }
    case 'ERROR': {
      return {
        ...state,
        ...action.payload,
        isError: true,
      };
    }
    default: {
      throw Error('Unknown action: ' + (action as any).type);
    }
  }
};

export default function CatalogProvider({ children }: { children: ReactNode }) {
  const [catalog, dispatch] = useReducer(catalogReducer, initialCatalog);

  // Memoize slices so unchanged slices keep referential stability for consumers.
  const products = useMemo(() => catalog.products, [catalog.products]);
  const view = useMemo(() => catalog.view, [catalog.view]);
  const ux = useMemo(() => catalog.ux, [catalog.ux]);
  const cart = useMemo(() => catalog.cart, [catalog.cart]);
  const stateValue = useMemo(() => catalog, [catalog]);
  const dispatchValue = useMemo(() => dispatch, [dispatch]); // dispatch is stable but memo for symmetry

  return (
    <CatalogDispatchContext.Provider value={dispatchValue}>
      <CatalogStateContext.Provider value={stateValue}>
        <ProductsContext.Provider value={products}>
          <ViewContext.Provider value={view}>
            <UxContext.Provider value={ux}>
              <CartContext.Provider value={cart}>{children}</CartContext.Provider>
            </UxContext.Provider>
          </ViewContext.Provider>
        </ProductsContext.Provider>
      </CatalogStateContext.Provider>
    </CatalogDispatchContext.Provider>
  );
}
